import React, { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { XMarkIcon, CloudArrowUpIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import {
  RelationshipType,
  LineageType,
  type FamilyMember,
  type FamilyTreeOwner,
  impliedGender,
  spouseOrdinal,
  familyTreeService,
} from '../../services/family-tree.service'
import { displayLabel, ordinalSuffix } from './MemberCard'
import { ADDRESS_STATE_NAMES, getCitiesForStateName } from '../../constants/malaysian-states'

interface EditMemberModalProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  member: FamilyMember | null
  owner: FamilyTreeOwner | null
  members: FamilyMember[]
}

type DocType = 'BIRTH_CERTIFICATE' | 'IC_PASSPORT'

interface FormValues {
  firstName: string
  lastName: string
  gender: 'MALE' | 'FEMALE'
  dateOfBirth: string
  isDeceased: string
  dateOfDeath: string
  lineageType: LineageType
  passportNumber: string
  email: string
  address: string
  city: string
  state: string
  postalCode: string
}

const LINEAGE_RELS = new Set<RelationshipType>([
  RelationshipType.FATHER,
  RelationshipType.MOTHER,
  RelationshipType.CHILD,
  RelationshipType.SIBLING,
  RelationshipType.FATHER_IN_LAW,
  RelationshipType.MOTHER_IN_LAW,
])
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf'
const MAX_BYTES = 10 * 1024 * 1024

function toDateInput(d: string | null): string {
  if (!d) return ''
  // Accept ISO; trim to yyyy-mm-dd
  return d.length >= 10 ? d.slice(0, 10) : ''
}

export default function EditMemberModal({
  isOpen,
  onClose,
  onSaved,
  member,
  owner,
  members,
}: EditMemberModalProps) {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [uploading, setUploading] = useState<DocType | null>(null)
  const birthFileRef = useRef<HTMLInputElement>(null)
  const icFileRef = useRef<HTMLInputElement>(null)

  const { register, handleSubmit, reset, watch, setValue, formState } = useForm<FormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      gender: 'MALE',
      dateOfBirth: '',
      isDeceased: 'false',
      dateOfDeath: '',
      lineageType: LineageType.BIOLOGICAL,
      passportNumber: '',
      email: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
    },
  })

  const rel = member?.relationship
  const ownerGender = owner?.gender ?? null
  const genderLocked = useMemoGenderLock(rel, ownerGender)
  const watchedIsDeceased = watch('isDeceased')
  const watchState = watch('state')

  useEffect(() => {
    if (isOpen && member) {
      reset({
        firstName: member.firstName,
        lastName: member.lastName ?? '',
        gender: member.gender,
        dateOfBirth: toDateInput(member.dateOfBirth),
        isDeceased: member.isDeceased ? 'true' : 'false',
        dateOfDeath: toDateInput(member.dateOfDeath),
        lineageType: member.lineageType,
        passportNumber: member.passportNumber ?? '',
        email: member.email ?? '',
        address: member.address ?? '',
        city: member.city ?? '',
        state: member.state ?? '',
        postalCode: member.postalCode ?? '',
      })
    }
  }, [isOpen, member, reset])

  useEffect(() => {
    if (genderLocked) setValue('gender', genderLocked)
  }, [genderLocked, setValue])

  if (!isOpen || !member) return null

  const labelKey = displayLabel(member.relationship, member.gender, ownerGender)
  const spouseOrd = member.relationship === RelationshipType.SPOUSE ? spouseOrdinal(member, members) : null
  const suffix = ordinalSuffix(spouseOrd)

  const validateFile = (f: File): string | null => {
    if (f.size > MAX_BYTES) return t('familyTree.err.fileTooLarge')
    const ok = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(f.type)
    return ok ? null : t('familyTree.err.unsupportedType')
  }

  const onPickFile = (docType: DocType) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f || !member) return
    const err = validateFile(f)
    if (err) {
      toast.error(err)
      if (e.target) e.target.value = ''
      return
    }
    setUploading(docType)
    try {
      await familyTreeService.uploadDocument(member.id, f, docType)
      toast.success(t('familyTree.upload'))
      onSaved()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('familyTree.err.unsupportedType'))
    } finally {
      setUploading(null)
      if (e.target) e.target.value = ''
    }
  }

  const onRemoveDoc = async (docType: DocType) => {
    if (!member) return
    setUploading(docType)
    try {
      await familyTreeService.removeDocument(member.id, docType)
      toast.success(t('familyTree.removeDocument'))
      onSaved()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error')
    } finally {
      setUploading(null)
    }
  }

  const onSubmit = async (data: FormValues) => {
    if (!member) return
    const isDeceasedBool = String(data.isDeceased) === 'true'
    setSubmitting(true)
    try {
      await familyTreeService.updateMember(member.id, {
        firstName: data.firstName.trim(),
        lastName: data.lastName?.trim() || undefined,
        gender: data.gender,
        dateOfBirth: data.dateOfBirth || undefined,
        isDeceased: isDeceasedBool,
        dateOfDeath: isDeceasedBool ? data.dateOfDeath || undefined : undefined,
        lineageType: data.lineageType,
        passportNumber: data.passportNumber?.trim() || undefined,
        email: data.email?.trim() || undefined,
        address: data.address?.trim() || undefined,
        city: data.city?.trim() || undefined,
        state: data.state?.trim() || undefined,
        postalCode: data.postalCode?.trim() || undefined,
      })
      toast.success(t('familyTree.save'))
      onSaved()
      onClose()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  const showLineage = !!rel && LINEAGE_RELS.has(rel)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-200 p-5">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">{t('familyTree.editMember')}</h2>
            <p className="text-xs capitalize text-primary-700">
              {t(`familyTree.rels.${labelKey}`)}{suffix}
              {member.targetSpouseId ? ` · ${t('familyTree.inLaws')}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
            disabled={submitting}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 p-5">
          {/* Personal details */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.firstName')} <span className="text-red-500">*</span>
              </label>
              <input
                className={`input mt-1 w-full ${formState.errors.firstName ? 'border-red-300' : ''}`}
                {...register('firstName', { required: true, minLength: 1 })}
              />
              {formState.errors.firstName && (
                <p className="mt-1 text-xs text-red-600">{t('familyTree.firstName')}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.lastName')}
              </label>
              <input className="input mt-1 w-full" {...register('lastName')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.gender')}
              </label>
              <select
                className="input mt-1 w-full disabled:bg-neutral-100"
                disabled={!!genderLocked}
                {...register('gender')}
              >
                <option value="MALE">{t('familyTree.male')}</option>
                <option value="FEMALE">{t('familyTree.female')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.dateOfBirth')}
              </label>
              <input type="date" className="input mt-1 w-full" {...register('dateOfBirth')} />
            </div>
          </div>

          {/* Lineage */}
          {showLineage && (
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.lineage')}
              </label>
              <div className="mt-1 flex gap-3">
                {[LineageType.BIOLOGICAL, LineageType.ADOPTIVE].map((lt) => (
                  <label
                    key={lt}
                    className="flex flex-1 cursor-pointer items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm"
                  >
                    <input
                      type="radio"
                      value={lt}
                      {...register('lineageType')}
                      className="text-primary-600"
                    />
                    {lt === LineageType.BIOLOGICAL
                      ? t('familyTree.biological')
                      : t('familyTree.adoptive')}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Life status */}
          <div>
            <label className="block text-sm font-medium text-neutral-700">
              {t('familyTree.lifeStatus')}
            </label>
            <div className="mt-1 flex gap-3">
              <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm">
                <input type="radio" value="false" {...register('isDeceased')} className="text-primary-600" />
                {t('familyTree.living')}
              </label>
              <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm">
                <input type="radio" value="true" {...register('isDeceased')} className="text-primary-600" />
                {t('familyTree.deceased')}
              </label>
            </div>
            {String(watchedIsDeceased) === 'true' && (
              <div className="mt-2">
                <label className="block text-sm font-medium text-neutral-700">
                  {t('familyTree.dateOfDeath')}
                </label>
                <input
                  type="date"
                  className="input mt-1 w-full sm:max-w-xs"
                  {...register('dateOfDeath')}
                />
              </div>
            )}
          </div>

          {/* Identity & address */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.passportNumber')}
              </label>
              <input className="input mt-1 w-full" {...register('passportNumber')} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.email')}
              </label>
              <input className="input mt-1 w-full" type="email" {...register('email')} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.address')}
              </label>
              <input className="input mt-1 w-full" {...register('address')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">{t('familyTree.city')}</label>
              <input
                className="input mt-1 w-full"
                list="edit-member-city-options"
                {...register('city')}
              />
              <datalist id="edit-member-city-options">
                {getCitiesForStateName(watchState || '').map((city) => (
                  <option key={city} value={city} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">{t('familyTree.state')}</label>
              <select className="input mt-1 w-full" {...register('state')}>
                <option value="">Select State</option>
                {ADDRESS_STATE_NAMES.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.postalCode')}
              </label>
              <input className="input mt-1 w-full" {...register('postalCode')} />
            </div>
          </div>

          {/* Documents (upload inline against existing id) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DocSlotExisting
              label={t('familyTree.birthCertificate')}
              url={member.birthCertificateUrl}
              uploading={uploading === 'BIRTH_CERTIFICATE'}
              inputRef={birthFileRef}
              onPick={onPickFile('BIRTH_CERTIFICATE')}
              onRemove={() => onRemoveDoc('BIRTH_CERTIFICATE')}
              t={t}
            />
            <DocSlotExisting
              label={t('familyTree.nricPassport')}
              url={member.nricPassportUrl}
              uploading={uploading === 'IC_PASSPORT'}
              inputRef={icFileRef}
              onPick={onPickFile('IC_PASSPORT')}
              onRemove={() => onRemoveDoc('IC_PASSPORT')}
              t={t}
            />
          </div>
          <p className="text-xs text-neutral-400">{t('familyTree.maxSize')}</p>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-neutral-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              {t('familyTree.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('familyTree.saving') : t('familyTree.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function useMemoGenderLock(
  rel: RelationshipType | undefined,
  ownerGender: 'MALE' | 'FEMALE' | null,
): 'MALE' | 'FEMALE' | null {
  if (!rel) return null
  if (rel === RelationshipType.SPOUSE) {
    return ownerGender === 'MALE' ? 'FEMALE' : ownerGender === 'FEMALE' ? 'MALE' : null
  }
  return impliedGender(rel)
}

function DocSlotExisting({
  label,
  url,
  uploading,
  inputRef,
  onPick,
  onRemove,
  t,
}: {
  label: string
  url: string | null | undefined
  uploading: boolean
  inputRef: React.RefObject<HTMLInputElement | null>
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemove: () => void
  t: (k: string) => string
}) {
  return (
    <div className="rounded-md border border-neutral-200 p-3">
      <p className="text-sm font-medium text-neutral-700">{label}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!url && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            {uploading ? t('familyTree.uploading') : t('familyTree.upload')}
          </button>
        )}
        {url && (
          <>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              {t('familyTree.viewDocument')}
            </a>
            <button
              type="button"
              onClick={onRemove}
              disabled={uploading}
              className="text-xs text-red-600 hover:underline disabled:opacity-60"
            >
              {t('familyTree.removeDocument')}
            </button>
          </>
        )}
        <input ref={inputRef} type="file" accept={ACCEPT} onChange={onPick} className="hidden" />
      </div>
    </div>
  )
}
